/**
 * ProviderAccountPage — provider self-service: change password + edit limited fields.
 */
import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Settings, Lock, Save } from 'lucide-react';
import { getProviderMe, editProviderMe, changeProviderPassword } from '@/services/realtimeApi';
import { providerStatusLabelEs } from '@/components/workflow/labels';

const ProviderAccountPage = () => {
  const { getAuthHeaders, user } = useAuth();
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({ availability: '', phone: '', operational_notes: '' });
  const [pwd, setPwd] = useState({ current: '', next: '', confirm: '' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const m = await getProviderMe(getAuthHeaders);
        setMe(m);
        setForm({ availability: m.availability || 'available', phone: m.phone || '', operational_notes: m.operational_notes || '' });
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.debug('[ProviderAccount] load profile failed', err);
        }
      }
    })();
  }, [getAuthHeaders]);

  const saveProfile = async () => {
    setBusy(true);
    try {
      const updated = await editProviderMe(getAuthHeaders, form);
      setMe(updated);
      toast.success('Perfil actualizado');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setBusy(false);
    }
  };

  const savePassword = async () => {
    if (pwd.next.length < 6) { toast.error('Mínimo 6 caracteres'); return; }
    if (pwd.next !== pwd.confirm) { toast.error('Las contraseñas no coinciden'); return; }
    setBusy(true);
    try {
      await changeProviderPassword(getAuthHeaders, pwd.current, pwd.next);
      toast.success('Contraseña actualizada');
      setPwd({ current: '', next: '', confirm: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setBusy(false);
    }
  };

  if (!me) return <p className="text-sm text-slate-400">Cargando…</p>;

  return (
    <div className="space-y-4 max-w-2xl">
      <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
        <Settings className="w-6 h-6 text-blue-600" />
        Mi cuenta
      </h1>

      {user?.force_password_change && (
        <Card className="p-3 bg-amber-50 border-amber-300 text-sm text-amber-800" data-testid="account-force-pwd">
          Por seguridad, define una nueva contraseña antes de seguir operando.
        </Card>
      )}

      <Card className="p-5 space-y-3" data-testid="account-profile-card">
        <h2 className="text-sm font-bold text-slate-900">Datos operativos</h2>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-[10px] uppercase text-slate-500">Nombre</p>
            <p className="text-sm font-semibold">{me.name}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase text-slate-500">Email</p>
            <p className="text-sm font-semibold">{me.email}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase text-slate-500">Rol</p>
            <p className="text-sm font-semibold">{me.role === 'payment_agent' ? 'Agente de Pago' : me.role === 'ticket_provider' ? 'Proveedor de Tickets' : me.role}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase text-slate-500">Estado</p>
            <p className="text-sm font-semibold">{providerStatusLabelEs(me.status)}</p>
          </div>
        </div>
        <div className="space-y-2 pt-2 border-t">
          <label className="text-[10px] uppercase text-slate-500">Disponibilidad</label>
          <select value={form.availability} onChange={(e) => setForm({ ...form, availability: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" data-testid="account-availability">
            <option value="available">disponible</option>
            <option value="busy">ocupado</option>
            <option value="offline">desconectado</option>
          </select>
          <Input placeholder="Teléfono" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="account-phone" />
          <textarea placeholder="Notas operativas" value={form.operational_notes} onChange={(e) => setForm({ ...form, operational_notes: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm min-h-[60px]" data-testid="account-notes" />
          <Button onClick={saveProfile} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="account-save"><Save className="w-3.5 h-3.5 mr-1" />Guardar</Button>
        </div>
      </Card>

      <Card className="p-5 space-y-3" data-testid="account-password-card">
        <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2"><Lock className="w-4 h-4" />Cambiar contraseña</h2>
        <Input type="password" placeholder="Contraseña actual" value={pwd.current} onChange={(e) => setPwd({ ...pwd, current: e.target.value })} data-testid="account-pwd-current" />
        <Input type="password" placeholder="Nueva contraseña (mín. 6)" value={pwd.next} onChange={(e) => setPwd({ ...pwd, next: e.target.value })} data-testid="account-pwd-new" />
        <Input type="password" placeholder="Confirma nueva contraseña" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} data-testid="account-pwd-confirm" />
        <Button onClick={savePassword} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="account-pwd-save"><Lock className="w-3.5 h-3.5 mr-1" />Actualizar contraseña</Button>
      </Card>
    </div>
  );
};

export default ProviderAccountPage;
